/**
 * How a BOQ shows the GST already inside its prices (#499).
 *
 * The server sends a line's `gstRateBp` and `gstAmount` only when its unit price
 * is a catalogue price; for a typed price, a fixed template price or a vendor
 * quote they are null. Null is shown as a dash, never as 0% or ₹0.00, and a
 * total counts only the lines that have a figure.
 */

import { formatUom } from "@/features/catalog/utils/uom";

/**
 * Own `Intl.NumberFormat` copy of `formatCurrency` in boq-api.service. Not
 * imported from there because several suites mock that whole service, and the
 * mock has no `formatCurrency`.
 */
function formatPaise(amountPaise: number, currencyCode: string): string {
	return new Intl.NumberFormat("en-IN", {
		style: "currency",
		currency: currencyCode,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amountPaise / 100);
}

/** The tooltip on every "GST incl." heading: it is part of the price, not extra. */
export const GST_INCLUDED_HINT =
	"The part of the price that is GST. It is already included in the price, not added on top.";

/** Shown wherever a GST figure is not known. */
export const NO_GST = "—";

/** "18%" or "2.5%" from basis points; a dash for null or undefined. */
export function formatGstRate(rateBp: number | null | undefined): string {
	if (rateBp === null || rateBp === undefined) return NO_GST;
	return `${Number((rateBp / 100).toFixed(2))}%`;
}

/** The GST amount in rupees from paise; a dash for null or undefined. */
export function formatGstAmount(
	amountPaise: number | null | undefined,
	currencyCode = "INR",
): string {
	if (amountPaise === null || amountPaise === undefined) return NO_GST;
	return formatPaise(amountPaise, currencyCode);
}

export interface GstIncludedSummary {
	/** Sum of the lines' (already rounded) GST amounts; null when no line has one. */
	amountPaise: number | null;
	lines: number;
	linesWithGst: number;
}

/** Sums line GST amounts as the server rounded them; nothing is re-rounded. */
export function summariseGstIncluded(
	lines: readonly { gstAmount?: number | null }[],
): GstIncludedSummary {
	let amountPaise: number | null = null;
	let linesWithGst = 0;
	for (const line of lines) {
		if (line.gstAmount === null || line.gstAmount === undefined) continue;
		amountPaise = (amountPaise ?? 0) + line.gstAmount;
		linesWithGst += 1;
	}
	return { amountPaise, lines: lines.length, linesWithGst };
}

/**
 * The "GST included" figure for a total: "₹1,800.00", with " (on 2 of 3 lines)"
 * when some lines have no figure, and a dash when none does. `coverageText`
 * words the "on 2 of 3 lines" part for a caller that translates it; the default
 * is English.
 */
export function formatGstIncluded(
	summary: GstIncludedSummary,
	currencyCode = "INR",
	coverageText: (linesWithGst: number, lines: number) => string = (
		linesWithGst,
		lines,
	) => `on ${linesWithGst} of ${lines} lines`,
): string {
	if (summary.amountPaise === null) return NO_GST;
	const amount = formatPaise(summary.amountPaise, currencyCode);
	return summary.linesWithGst < summary.lines
		? `${amount} (${coverageText(summary.linesWithGst, summary.lines)})`
		: amount;
}

/**
 * "per man-day" for a product whose unit basis reads cleanly, else "per Nos"
 * from the line's unit of measure; null when the line has neither.
 */
export function perUnitText(line: {
	uom?: string | null;
	unitLabel?: string | null;
}): string | null {
	if (line.unitLabel) return `per ${line.unitLabel}`;
	return line.uom ? `per ${formatUom(line.uom)}` : null;
}
